import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import {
  Card,
  Text,
} from './style';
import Subscription from '../SubscriptionDetails';
import { RootState } from '../../store';
import RefundModal from '../RefundModal';
import CancellationModal from '../CancellationModal';
import RefundFormModal from '../RefundFormModal';
import PauseModal from '../PauseModal';
import { hasAccess } from '../../services/utils';
import api from '../../services/api';
import { SubscriptionCancellationType } from '../../types/SubscriptionCancellation';

const PERSONAL_CANCELLATION_FORM_URL = 'https://forms.gle/wXSaQCirZdkjZeK2A';
const ESSENTIAL_CANCELLATION_WITHIN_GUARANTEE_FORM_URL = 'https://forms.gle/d2oFTSTcZBRcPuGn6';
const ESSENTIAL_DEACTIVATE_RENEWAL_FORM_URL = 'https://forms.gle/TG1E8QFgFmQqX9NE6';

export default function SubscriptionInfo() {
  const subscription = useSelector((state: RootState) => state.subscription.subscription);
  const auth = useSelector((state: RootState) => state.auth);
  const { user } = auth;
  const isPersonalUser = hasAccess(user, ['PERSONAL']);
  const isEssentialUser = hasAccess(user, ['ESSENTIAL']);
  const isBasicUser = hasAccess(user, ['BASIC']);
  const [isRefundable, setIsRefundable] = useState(false);
  const [refundRequest, setRefundRequest] = useState<SubscriptionCancellationType>();

  const [showRefundModal, setShowRefundModal] = useState(false);
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const [showRefundFormModal, setShowRefundFormModal] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);

  useEffect(() => {
    if (!subscription?.start_at || subscription?.status !== 'active') {
      setIsRefundable(false);
      return;
    }

    const dateStart = new Date(subscription.start_at);
    const dateNow = new Date();
    const diffTime = Math.abs(dateNow.getTime() - dateStart.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    setIsRefundable(diffDays <= 15);
  }, [subscription]);

  useEffect(() => {
    if (!isBasicUser || !subscription?.id) {
      setRefundRequest(undefined);
      return;
    }

    api.get(`subscription_cancellations/${subscription?.id}/requests`)
      .then((response) => {
        if (response.data?.type === 'REFUND') setRefundRequest(response.data);
      });
  }, [isBasicUser, subscription?.id]);

  const renderExternalFormLink = (url: string, label = 'Clique aqui.') => (
    <a
      href={url}
      style={{ color: 'red' }}
      target="_blank"
      rel="noreferrer"
    >
      {label}
    </a>
  );

  let cancellationActionLink = (
    <a href="#assinatura" style={{ color: 'red' }} onClick={() => setShowCancellationModal(true)}>
      clique aqui.
    </a>
  );

  if (isPersonalUser) {
    cancellationActionLink = renderExternalFormLink(PERSONAL_CANCELLATION_FORM_URL, 'clique aqui.');
  }

  const refundActionLink = (
    <a href="#assinatura" style={{ color: 'red' }} onClick={() => setShowRefundFormModal(true)}>
      Clique aqui.
    </a>
  );
  const shouldShowRefundRequest = isBasicUser && isRefundable && !refundRequest;
  const shouldShowRefundStatus = isBasicUser && !!refundRequest;
  const nextBillingDateLabel = subscription?.next_billing_at
    ? format(new Date(subscription.next_billing_at), 'dd/MM/yyyy')
    : '--/--/----';
  const shouldShowEssentialCancellation = subscription?.status === 'active' && isEssentialUser;
  const shouldShowDefaultCancellation = subscription?.status === 'active'
    && hasAccess(user, ['START', 'BASIC', 'PERSONAL']);

  return (
    <>
      <Card data-testid="modal">
        <Subscription />

        {
          subscription?.plan
          && (
            <Text>
              <h5>ALTERAÇÕES E OUTROS</h5>

              <p>
                Caso desejar modificar algo, como trocar o plano, ou tratar de
                outro assunto sobre sua assinatura, escreva para
                &nbsp;
                <a href="mailto:assinatura@corridaperfeita.com" style={{ color: 'white' }}>assinatura@corridaperfeita.com</a>
                .
              </p>

              {
                shouldShowEssentialCancellation
                && (
                  isRefundable ? (
                    <>
                      <h5>CANCELAMENTO OU DESATIVAÇÃO DA RENOVAÇÃO</h5>
                      <p>
                        Não queremos te ver partir! Mas se precisar cancelar sua adesão ou apenas
                        desativar sua renovação futura,
                        {' '}
                        {renderExternalFormLink(
                          ESSENTIAL_CANCELLATION_WITHIN_GUARANTEE_FORM_URL,
                          'clique aqui.',
                        )}
                      </p>
                    </>
                  ) : (
                    <>
                      <h5>DESATIVAÇÃO DA RENOVAÇÃO</h5>
                      <p>
                        Se deseja desativar a próxima renovação do seu plano, prevista para
                        {' '}
                        {nextBillingDateLabel}
                        ,
                        {' '}
                        {renderExternalFormLink(
                          ESSENTIAL_DEACTIVATE_RENEWAL_FORM_URL,
                          'clique aqui.',
                        )}
                      </p>
                    </>
                  )
                )
              }

              {
                shouldShowDefaultCancellation
                && (
                  <>
                    <h5>CANCELAMENTO</h5>
                    <p>
                      Não queremos te ver partir! Mas se precisar cancelar sua assinatura,
                      {' '}
                      {cancellationActionLink}
                    </p>
                  </>
                )
              }

              {
                shouldShowRefundRequest
                && (
                  <>
                    <h5>PEDIDO DE REEMBOLSO</h5>
                    <p>
                      Para solicitar reembolso previsto no período de até 15 dias após o início de seu plano. &nbsp;
                      {refundActionLink}
                    </p>
                  </>
                )
              }

              {
                shouldShowRefundStatus
                && (
                  <>
                    <h5>PEDIDO DE REEMBOLSO</h5>
                    <p>
                      Você pediu o reembolso da garantia no dia&nbsp;
                      {refundRequest?.created_at ? format(new Date(refundRequest?.created_at), 'dd/MM/yyyy') : '--/--/----'}
                      .&nbsp;
                      O nosso prazo para atendimento de sua solicitação é de 5 (cinco) dias úteis.
                    </p>
                  </>
                )
              }

              <RefundModal
                show={showRefundModal && !showPauseModal && !showRefundFormModal}
                toggle={() => setShowRefundModal(!showRefundModal)}
                setShowRefundFormModal={setShowRefundFormModal}
                setShowPauseModal={setShowPauseModal}
              />

              <RefundFormModal
                show={showRefundFormModal}
                toggle={() => setShowRefundFormModal(!showRefundFormModal)}
              />

              <PauseModal
                show={showPauseModal}
                toggle={() => setShowPauseModal(!showPauseModal)}
              />

              <CancellationModal
                show={showCancellationModal}
                toggle={() => setShowCancellationModal(!showCancellationModal)}
              />
            </Text>
          )
        }
      </Card>
    </>
  );
}
